/**
 * useDjBillboard — the radio VIDEO HOST cameo, ported from the website's
 * useVideoHost to HypeTunes_World. The station's DJ steps onto the big city
 * billboards as short CAMEOS for DYNAMIC moments, speaks a live line, then
 * steps off — the billboard art returns underneath.
 *
 * WHY dynamic-only: SCHEDULED narrations are deliberately AUDIO-ONLY. Their mp3
 * durations ARE the deterministic playlist offsets every listener's playhead is
 * computed from; a live on-camera retelling has a different duration per viewer
 * and drifts the World's playhead off the shared station clock (and off website
 * listeners). This was tried and reverted 2026-07-17 — see useVideoHost.js. So
 * the World only cameos on dynamic drops the schedule doesn't depend on.
 *
 * The Anam stream binds to a hidden <video id="dj-cameo-video"> (audio plays;
 * pixels are sampled into a VideoTexture on the billboards by CityBillboards).
 * The station music ducks under the DJ and comes back when they step off.
 * One-way session (disableInputAudio) — the DJ talks, nobody talks back.
 *
 * Server enforces access (paid plan) + the appearance budget (a 429 = "stay on
 * audio, back off"). Admin bypasses both, so it's testable.
 */
import { useCallback, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { audioEngine } from '../services/audioEngine';
import { useWorldStore } from '../state/worldStore';
import { IS_MOBILE, VIDEO_OFF } from '../config';

// THE PHONE KEEPS THE DJ, NOT THE CAMERA.
//
// A cameo is a live WebRTC session, a hardware video decoder, and a VideoTexture
// sampled onto the billboards EVERY frame — stacked on top of a 3D city that is
// already at this device's ceiling. iOS answers that by killing the tab, which
// is the crash seen when the radio host tries to appear.
//
// The DJ still talks on a phone: the station's scheduled narrations are audio
// anyway (they have to be — their durations ARE the shared playlist offsets, see
// the note at the top of this file), so what a phone loses is the picture, not
// the presenter. Skipping it before the token request also means a phone never
// opens a billable Anam session it cannot render.
const CAMEO_ON_MOBILE = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('djvideo') === '1';
const CAMEO_ENABLED = !IS_MOBILE || CAMEO_ON_MOBILE;

const VIDEO_EL_ID = 'dj-cameo-video';
const EXIT_GRACE_MS = 2500;          // step off this soon after a line finishes
const DEFAULT_BACKOFF_MS = 5 * 60_000;
const ACTIVITY_WINDOW_MS = 10 * 60_000;   // no auto-cameos for an idle tab
const FIRST_DELAY_MS = 25_000;       // settle into the world before the first drop
const AUTO_EVERY_MS = 4 * 60_000;    // cadence of dynamic drops while present

// The Anam SDK, fetched ONCE per page rather than once per cameo attempt.
//
// A successful `import()` is cached by the browser's module registry, but a
// FAILED one is not — so every retry re-hit esm.sh, and esm.sh rate-limits.
// A run of failures therefore became a run of 429s that generated more 429s.
// Holding the promise caches the success; clearing it on failure still allows a
// later attempt, but that attempt is now rate-limited by the caller's backoff
// rather than firing straight back at the CDN.
let sdkPromise = null;
const loadSdk = () => {
  if (!sdkPromise) {
    sdkPromise = import(/* @vite-ignore */ 'https://esm.sh/@anam-ai/js-sdk@latest')
      .catch((e) => { sdkPromise = null; throw e; });
  }
  return sdkPromise;
};

const speechMs = (text) => {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean).length;
  return Math.min(30000, Math.max(1800, Math.round((words / 165) * 60000) + 800));
};

export default function useDjBillboard() {
  const setDjCameo = useWorldStore((s) => s.setDjCameo);

  const phaseRef = useRef('idle');            // idle | armed | connecting | live
  const stationIdRef = useRef(null);
  const clientRef = useRef(null);
  const sessionRef = useRef(null);
  const endedRef = useRef(false);
  const liveRef = useRef(false);
  const runRef = useRef(0);                   // cancellation token for openCameo
  const aliveRef = useRef(true);
  const backoffRef = useRef(0);
  const closingRef = useRef(false);           // re-entrancy guard, see closeCameo
  const lastActivityRef = useRef(Date.now());
  const lingerTimerRef = useRef(null);
  const capTimerRef = useRef(null);
  const talkTimerRef = useRef(null);

  const setPhase = useCallback((p, extra = {}) => {
    phaseRef.current = p;
    if (aliveRef.current) setDjCameo({ phase: p, ...extra });
  }, [setDjCameo]);

  const report = useCallback(async (body) => {
    const sid = sessionRef.current;
    if (!sid) return;
    try { await api.anamSessionReport(sid, body); } catch { /* best effort */ }
  }, []);

  const clearTimers = () => {
    [lingerTimerRef, capTimerRef, talkTimerRef].forEach((r) => { if (r.current) { clearTimeout(r.current); r.current = null; } });
  };

  // CLOSING RE-ENTERS ITSELF. stopStreaming() emits CONNECTION_CLOSED
  // SYNCHRONOUSLY, and our listener for that event calls straight back in here.
  // This used to stop the client while `liveRef` was still true and `clientRef`
  // still set, so the listener's `if (liveRef.current)` guard passed and closed
  // again — and again — until the stack blew. Each pass also fired an Anam
  // client metric, which is why the crash presented as a flood of 429s from
  // api.anam.ai rather than as the recursion it actually was.
  //
  // Two guards, because one is not enough: the flag stops re-entry from ANY
  // event the SDK emits during teardown, and the ordering below means that even
  // if something slips past the flag, the listener's own guard is already false.
  const closeCameo = useCallback((next = 'armed') => {
    if (closingRef.current) return;
    closingRef.current = true;
    try {
      runRef.current += 1;                     // supersede any in-flight connect
      clearTimers();
      // Drop our references BEFORE stopping the client, not after.
      const client = clientRef.current;
      clientRef.current = null;
      liveRef.current = false;
      try { client?.stopStreaming?.(); } catch { /* noop */ }
      audioEngine.duck(false);                 // release the music duck
      if (!endedRef.current && sessionRef.current) { endedRef.current = true; report({ event: 'ended' }); }
      sessionRef.current = null;
      setPhase(next === 'idle' ? 'idle' : next, { videoEl: null });
    } finally {
      closingRef.current = false;
    }
  }, [report, setPhase]);
  const closeRef = useRef(closeCameo);
  useEffect(() => { closeRef.current = closeCameo; }, [closeCameo]);

  const armLinger = useCallback((extraMs = 0) => {
    if (lingerTimerRef.current) clearTimeout(lingerTimerRef.current);
    lingerTimerRef.current = setTimeout(() => closeRef.current?.('armed'), extraMs + EXIT_GRACE_MS);
  }, []);

  const speak = useCallback((text) => {
    const client = clientRef.current;
    const line = String(text || '').trim();
    if (!client || !liveRef.current || !line) return;
    try {
      const t = client.talk(line);
      if (t?.catch) t.catch((e) => console.warn('[DjBillboard] talk rejected:', e?.message || e));
      audioEngine.duck(true);                  // dip the station under the DJ
      if (talkTimerRef.current) clearTimeout(talkTimerRef.current);
      talkTimerRef.current = setTimeout(() => audioEngine.duck(false), speechMs(line));
      armLinger(speechMs(line));
    } catch { /* line lost */ }
  }, [armLinger]);

  const openCameo = useCallback(async () => {
    if (!CAMEO_ENABLED) return;
    const stationId = stationIdRef.current;
    if (phaseRef.current !== 'armed' || !stationId) return;
    if (Date.now() < backoffRef.current) return;
    if (Date.now() - lastActivityRef.current > ACTIVITY_WINDOW_MS) return; // idle tab, cost care
    // Nobody is in the world yet. DjBillboardHost mounts at App level, so
    // without this the DJ performs to the loading screen — a billed Anam
    // session for an audience that hasn't arrived.
    if (!useWorldStore.getState().entered) return;
    // Same care for a backgrounded tab: a cameo nobody can see still bills.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    const run = ++runRef.current;
    const cancelled = () => run !== runRef.current || !aliveRef.current;
    setPhase('connecting');
    let client = null;
    try {
      const mod = await loadSdk();
      if (cancelled()) throw Object.assign(new Error('cancelled'), { cancelled: true });
      const createClient = mod.createClient || mod.default?.createClient;
      const AnamEvent = mod.AnamEvent || mod.default?.AnamEvent || {};
      if (!createClient) throw new Error('engine load failed');

      const { res, data } = await api.anamRadioSessionToken(stationId);
      if (!res.ok || !data.success) {
        if (res.status === 429) backoffRef.current = Date.now() + ((data.retryAfterSec || 300) * 1000);
        else if (res.status === 503) backoffRef.current = Date.now() + DEFAULT_BACKOFF_MS;
        const err = new Error(data.error || `cameo denied (${res.status})`);
        err.quiet = res.status === 429 || res.status === 503;
        throw err;
      }
      sessionRef.current = data.sessionId;
      endedRef.current = false;
      if (cancelled()) throw Object.assign(new Error('cancelled'), { cancelled: true });

      client = createClient(data.sessionToken, { disableInputAudio: true });
      clientRef.current = client;

      let readyResolve;
      const sessionReady = new Promise((resolve) => { readyResolve = resolve; });
      try {
        if (AnamEvent.SESSION_READY) client.addListener(AnamEvent.SESSION_READY, (sid) => {
          report({ anamSessionId: sid, event: 'started' });   // powers the server stop-backstop
          readyResolve(true);
        });
        if (AnamEvent.CONNECTION_CLOSED) client.addListener(AnamEvent.CONNECTION_CLOSED, () => {
          if (liveRef.current) closeRef.current?.('armed');
        });
        const onSpeechDone = () => { audioEngine.duck(false); armLinger(); };
        if (AnamEvent.MESSAGE_HISTORY_UPDATED) client.addListener(AnamEvent.MESSAGE_HISTORY_UPDATED, onSpeechDone);
        if (AnamEvent.TALK_STREAM_INTERRUPTED) client.addListener(AnamEvent.TALK_STREAM_INTERRUPTED, onSpeechDone);
      } catch { /* listeners best-effort */ }

      const videoEl = document.getElementById(VIDEO_EL_ID);
      if (!videoEl) throw Object.assign(new Error('cancelled'), { cancelled: true });
      await client.streamToVideoElement(VIDEO_EL_ID);
      if (cancelled()) throw Object.assign(new Error('cancelled'), { cancelled: true });

      if (AnamEvent.SESSION_READY) {
        const ready = await Promise.race([sessionReady, new Promise((r) => setTimeout(() => r(false), 10000))]);
        if (!ready) throw new Error('session never became ready');
      } else {
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (cancelled()) throw Object.assign(new Error('cancelled'), { cancelled: true });

      liveRef.current = true;
      setPhase('live', { hostName: data.hostName || null, videoEl });
      capTimerRef.current = setTimeout(() => closeRef.current?.('armed'), Math.max(30, (data.maxSeconds || 120) - 5) * 1000);
      armLinger();

      // Ask the brain for an unprompted DJ drop (same persona/thread as chat),
      // then speak it on camera. greeting:true → a self-started line.
      try {
        const turn = await api.anamSessionTurn(sessionRef.current, { greeting: true });
        if (!cancelled() && turn?.data?.reply) speak(turn.data.reply);
        else armLinger();
      } catch { armLinger(); }
    } catch (err) {
      // EVERY FAILURE COSTS TIME. Only the 429 and 503 branches above used to
      // set a backoff, so any other failure — the SDK failing to load, the
      // session never becoming ready, a dropped connection — retried at full
      // cadence forever, and a failure that is itself rate-limiting (the CDN
      // 429ing) fed itself. A cancellation is not a failure and stays free.
      if (!err.cancelled && Date.now() >= backoffRef.current) {
        backoffRef.current = Date.now() + DEFAULT_BACKOFF_MS;
      }
      if (err.cancelled) console.info('[DjBillboard] cameo cancelled mid-connect');
      else if (err.quiet) console.info('[DjBillboard] cameo skipped:', err.message);
      else console.warn('[DjBillboard] cameo failed:', err.message, `— backing off ${Math.round((backoffRef.current - Date.now()) / 1000)}s`);
      audioEngine.duck(false);
      // Same ordering rule as closeCameo: clear the live flag and our reference
      // BEFORE stopping, because stopping emits CONNECTION_CLOSED synchronously
      // and that listener closes on `liveRef`.
      const dying = client || clientRef.current;
      if (clientRef.current === client) clientRef.current = null;
      liveRef.current = false;
      try { dying?.stopStreaming?.(); } catch { /* noop */ }
      if (sessionRef.current && !endedRef.current) { endedRef.current = true; report({ event: 'ended' }); }
      sessionRef.current = null;
      setPhase(phaseRef.current === 'idle' ? 'idle' : 'armed', { videoEl: null });
    }
  }, [setPhase, report, armLinger, speak]);

  // Resolve the station id from the now-playing payload, then check whether the
  // host is on-camera-capable + this viewer may watch → auto-arm (admin/paid).
  useEffect(() => {
    aliveRef.current = true;
    // Never arm on a phone: no status request, no session, no video element.
    if (!CAMEO_ENABLED) {
      console.info('[DjBillboard] mobile — radio host stays audio-only (?djvideo=1 to force)');
      return undefined;
    }
    let dead = false;
    const unsub = useWorldStore.subscribe((st) => {
      const id = st.nowPlaying?.station?.station_id;
      if (id && id !== stationIdRef.current) {
        stationIdRef.current = id;
        api.anamRadioStatus(id).then((d) => {
          if (dead || !d?.success || !d.available || !d.planOk) return;
          if (phaseRef.current === 'idle') setPhase('armed', { hostName: d.hostName || null });
        }).catch(() => { /* stays idle */ });
      }
    });
    // Prime from current state (subscribe only fires on change).
    const id0 = useWorldStore.getState().nowPlaying?.station?.station_id;
    if (id0) {
      stationIdRef.current = id0;
      api.anamRadioStatus(id0).then((d) => {
        if (dead || !d?.success || !d.available || !d.planOk) return;
        if (phaseRef.current === 'idle') setPhase('armed', { hostName: d.hostName || null });
      }).catch(() => {});
    }
    return () => { dead = true; unsub(); };
  }, [setPhase]);

  // Activity clock (auto-cameos pause for an idle tab).
  useEffect(() => {
    const mark = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener('pointerdown', mark, { capture: true, passive: true });
    window.addEventListener('keydown', mark, { capture: true, passive: true });
    return () => {
      window.removeEventListener('pointerdown', mark, { capture: true });
      window.removeEventListener('keydown', mark, { capture: true });
    };
  }, []);

  // Dynamic-moment scheduler: a first drop after the world settles, then a
  // steady cadence while present. Dev: press "v" to fire one now (?dj shows chrome).
  // Called through a REF, and the effect mounts ONCE — same trick this file
  // already uses for closeRef, and for the same reason.
  //
  // Depending on openCameo's identity here made the cameo self-restarting:
  // closing one calls setPhase, which rebuilds openCameo, which re-runs this
  // effect, which re-arms the 25s FIRST_DELAY timer. So every cameo scheduled
  // the next one ~25s later instead of the intended 4 minutes — a live Anam
  // session (and a metrics round-trip) every half minute, forever, including
  // on the loading screen.
  const openRef = useRef(openCameo);
  useEffect(() => { openRef.current = openCameo; }, [openCameo]);
  useEffect(() => {
    // Stills test (config.VIDEO_OFF): the cameo IS a video — no auto-fire, no
    // 'v'. The DJ stays on audio, which the schedule already treats as canon.
    if (VIDEO_OFF) return undefined;
    const fire = () => { if (phaseRef.current === 'armed') openRef.current?.(); };
    const first = setTimeout(fire, FIRST_DELAY_MS);
    const iv = setInterval(fire, AUTO_EVERY_MS);
    const onKey = (e) => { if (e.key === 'v') fire(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(first); clearInterval(iv); window.removeEventListener('keydown', onKey); };
  }, []);

  // Teardown: close any open cameo (reports ended → server stops Anam + refunds
  // the unused block) on unmount and on hard page exit.
  useEffect(() => {
    const flush = () => {
      const sid = sessionRef.current;
      if (!sid || endedRef.current) return;
      endedRef.current = true;
      try { api.anamSessionReport(sid, { event: 'ended' }); } catch { /* noop */ }
    };
    window.addEventListener('pagehide', flush);
    return () => {
      aliveRef.current = false;
      window.removeEventListener('pagehide', flush);
      closeRef.current?.('idle');
    };
  }, []);

  return { trigger: openCameo };
}
